import { API, Auth } from '../utils/amplifyWrapper';
import { toast } from 'react-toastify';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useHistory } from "react-router-dom";
import * as Sentry from '@sentry/react';
import { Radio, ArrowLeft, Upload, Image, Sparkles } from 'lucide-react';

import Spinner from '../components/Spinner';
import CreatePodcastSkeleton from '../components/skeletons/pages/CreatePodcastSkeleton';
import only from '../utils/Only';
import { useCreatePodcast } from '../http-hooks/podcast/usePodcast';
import { useIntegration } from '../http-hooks/integration/useIntegration';
import { useOrganisation } from '../http-hooks/organisation/useOrganisation';
import { useGlobalStore } from '../zustand-store/useGlobalStore';

import PODCAST_CATEGORIES from '../constants/podcast_categories.json';
import { Card } from '../components/ui/bootstrap';

const inputStyle = {
    fontSize: '13px',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid rgba(var(--bs-primary-rgb), 0.18)',
    background: 'rgba(255,255,255,0.80)',
    color: 'var(--bs-body-color)',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
};

const inputInvalidStyle = {
    ...inputStyle,
    borderColor: 'var(--bs-danger)',
};

const labelStyle = {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--bs-secondary-color)',
    marginBottom: 6,
    display: 'block',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
};

const sectionHeaderStyle = {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--bs-secondary-color)',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    marginBottom: 16,
};

function FocusInput({ style, ...props }) {
    return (
        <input
            {...props}
            style={style}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--bs-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(var(--bs-primary-rgb), 0.10)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = style.borderColor || 'rgba(var(--bs-primary-rgb), 0.18)'; e.currentTarget.style.boxShadow = 'none'; }}
        />
    );
}

function FocusSelect({ style, children, ...props }) {
    return (
        <select
            {...props}
            style={{ ...style, appearance: 'auto' }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--bs-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(var(--bs-primary-rgb), 0.10)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.18)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
            {children}
        </select>
    );
}

function FocusTextarea({ style, ...props }) {
    return (
        <textarea
            {...props}
            style={{ ...style, resize: 'vertical' }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--bs-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(var(--bs-primary-rgb), 0.10)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.18)'; e.currentTarget.style.boxShadow = 'none'; }}
        />
    );
}

function CreatePodcast() {
    const { org_id, id } = useParams();
    const history = useHistory();
    const createPodcastMutation = useCreatePodcast({ org_id, integration_id: id });
    const { data: integration, isLoading } = useIntegration({ org_id, integration_id: id });
    const { data: org } = useOrganisation({ org_id });
    const { register, reset, formState: { errors }, handleSubmit } = useForm();
    const toggleUpgradeModal = useGlobalStore((state) => state.toggleUpgradeModal);

    const [category, setCategory] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [posterFile, setPosterFile] = useState(null);
    const [posterError, setPosterError] = useState(null);
    const [posterDragOver, setPosterDragOver] = useState(false);

    const onCategoryChange = (event) => setCategory(event.target.value);

    const onSubmit = async (data) => {
        if (!posterFile) {
            setPosterError('Podcast image is required');
            return;
        }
        data = only(data, ['title', 'description', 'author', 'email', 'category', 'sub_category', 'is_explicit', 'is_automatic', 'is_published', 'poster_path']);
        data.is_explicit = !!data.is_explicit;
        data.is_automatic = !!data.is_automatic;
        data.is_published = !!data.is_published;
        setIsUpdating(true);
        try {
            const signedResponse = await API.get('main', '/podast/cover/signed-url', {
                queryStringParameters: { file_name: posterFile.name, content_type: posterFile.type }
            });
            await fetch(signedResponse.data.signed_url, { method: 'PUT', body: posterFile, redirect: 'follow' });
            data.poster_path = signedResponse.data.url;
            await createPodcastMutation.mutateAsync(data);
            history.push(`/organisation/${org_id}/integration/${id}/podcast`);
        } catch (error) {
            toast.error('Something went wrong while creating the Podcast channel.');
            Sentry.captureException(error);
        } finally {
            setIsUpdating(false);
        }
    };

    useEffect(() => {
        reset({ is_published: true });
        Auth.currentAuthenticatedUser({ bypassCache: true })
            .then(user => {
                reset({ email: user.attributes.email, author: user.attributes.given_name, is_published: true });
            })
            .catch(err => { toast.error('Something went wrong.'); Sentry.captureException(err); });
    }, [integration, reset]);

    const handlePosterDrop = (e) => {
        e.preventDefault();
        setPosterDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) { setPosterFile(file); setPosterError(null); }
    };

    if (isLoading) {
        return <CreatePodcastSkeleton />;
    }

    // Upgrade gate
    if (integration.gates?.can_create_podcast !== true) {
        return (
            <div>
                {/* Header */}
                <Card className="mb-4 p-4" shadow>
                    <div className="d-flex align-items-center gap-3">
                        <div style={{ width: 48, height: 48, borderRadius: '14px', background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 16px rgba(var(--bs-primary-rgb), 0.28)' }}>
                            <Radio size={22} color="white" />
                        </div>
                        <div>
                            <h4 className="mb-0 fw-bold text-body-emphasis" style={{ fontSize: '20px' }}>Create Podcast Show</h4>
                            <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>Start a new show to grow your audience with audio</p>
                        </div>
                    </div>
                </Card>

                <Card className="p-5 text-center">
                    <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(var(--bs-primary-rgb), 0.08)', border: '1px solid rgba(var(--bs-primary-rgb), 0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                        <Radio size={30} color="var(--bs-primary)" />
                    </div>
                    <h5 style={{ fontWeight: 700, color: 'var(--bs-emphasis-color)', marginBottom: 8 }}>Upgrade to publish podcasts</h5>
                    <p style={{ fontSize: '13px', color: 'var(--bs-secondary-color)', marginBottom: 24 }}>
                        Upgrade your plan to publish podcasts and submit them to Apple Podcasts, Spotify, and more.
                    </p>
                    <button
                        onClick={() => toggleUpgradeModal(true, { source: 'podcast_create', isManual: true })}
                        style={{ background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 24px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: '0 4px 14px rgba(var(--bs-primary-rgb), 0.28)' }}
                    >
                        <Sparkles size={15} /> View Plans
                    </button>
                </Card>
            </div>
        );
    }

    return (
        <div>
            {/* Page header */}
            <Card className="mb-4 p-4" shadow>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <button
                            type="button"
                            onClick={() => history.push(`/organisation/${org_id}/integration/${id}/podcast`)}
                            style={{ background: 'rgba(var(--bs-primary-rgb), 0.06)', border: '1px solid rgba(var(--bs-primary-rgb), 0.14)', borderRadius: 10, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--bs-secondary-color)', flexShrink: 0, transition: 'all 0.18s ease', padding: 0 }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--bs-primary)'; e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.30)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--bs-secondary-color)'; e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.14)'; }}
                        >
                            <ArrowLeft size={16} />
                        </button>
                        <div style={{ width: 48, height: 48, borderRadius: '14px', background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 16px rgba(var(--bs-primary-rgb), 0.28)' }}>
                            <Radio size={22} color="white" />
                        </div>
                        <div>
                            <h4 className="mb-0 fw-bold text-body-emphasis" style={{ fontSize: '20px' }}>Create Podcast Show</h4>
                            <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>Start a new show to grow your audience with audio</p>
                        </div>
                    </div>
                </div>
            </Card>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row g-4">
                    {/* Main form column */}
                    <div className="col-12 col-lg-8">

                        {/* General section */}
                        <Card className="p-4 mb-4">
                            <p style={sectionHeaderStyle}>General</p>
                            <div className="row g-3">
                                <div className="col-12">
                                    <label style={labelStyle}>Podcast Title <span style={{ color: 'var(--bs-danger)' }}>*</span></label>
                                    <FocusInput
                                        type="text"
                                        style={errors.title ? inputInvalidStyle : inputStyle}
                                        {...register('title', { required: true, minLength: 3 })}
                                    />
                                    {errors.title && (
                                        <span style={{ fontSize: '11px', color: 'var(--bs-danger)', marginTop: 4, display: 'block' }}>
                                            {errors.title.type === 'required' && 'Podcast title is required.'}
                                            {errors.title.type === 'minLength' && 'Title must be at least 3 characters.'}
                                        </span>
                                    )}
                                </div>

                                <div className="col-12">
                                    <label style={labelStyle}>Podcast Description <span style={{ color: 'var(--bs-danger)' }}>*</span></label>
                                    <FocusTextarea
                                        rows={3}
                                        style={inputStyle}
                                        {...register('description', { required: true })}
                                    />
                                </div>

                                <div className="col-12 col-md-6">
                                    <label style={labelStyle}>Author Name <span style={{ color: 'var(--bs-danger)' }}>*</span></label>
                                    <FocusInput
                                        type="text"
                                        style={errors.author ? inputInvalidStyle : inputStyle}
                                        {...register('author', { required: true, minLength: 3 })}
                                    />
                                    {errors.author && (
                                        <span style={{ fontSize: '11px', color: 'var(--bs-danger)', marginTop: 4, display: 'block' }}>
                                            {errors.author.type === 'required' && 'Author name is required.'}
                                            {errors.author.type === 'minLength' && 'Author name must be at least 3 characters.'}
                                        </span>
                                    )}
                                </div>

                                <div className="col-12 col-md-6">
                                    <label style={labelStyle}>Author Email <span style={{ color: 'var(--bs-danger)' }}>*</span></label>
                                    <FocusInput
                                        type="email"
                                        style={errors.email ? inputInvalidStyle : inputStyle}
                                        {...register('email', { required: true, pattern: /^\S+@\S+\.\S+$/i })}
                                    />
                                    {errors.email && (
                                        <span style={{ fontSize: '11px', color: 'var(--bs-danger)', marginTop: 4, display: 'block' }}>
                                            {errors.email.type === 'required' && 'Author email is required.'}
                                            {errors.email.type === 'pattern' && 'Please enter a valid email address.'}
                                        </span>
                                    )}
                                </div>

                                <div className="col-12 col-md-6">
                                    <label style={labelStyle}>Category <span style={{ color: 'var(--bs-danger)' }}>*</span></label>
                                    <FocusSelect
                                        style={errors.category ? inputInvalidStyle : inputStyle}
                                        {...register('category', { required: true })}
                                        onChange={onCategoryChange}
                                    >
                                        <option value=''>Select a category</option>
                                        {Object.keys(PODCAST_CATEGORIES).map((cat) => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </FocusSelect>
                                    {errors.category && (
                                        <span style={{ fontSize: '11px', color: 'var(--bs-danger)', marginTop: 4, display: 'block' }}>Category is required.</span>
                                    )}
                                </div>

                                <div className="col-12 col-md-6">
                                    <label style={labelStyle}>Sub-Category</label>
                                    <FocusSelect
                                        style={inputStyle}
                                        {...register('sub_category')}
                                        disabled={!category || PODCAST_CATEGORIES[category]?.length === 0}
                                    >
                                        {!!category && PODCAST_CATEGORIES[category].map((sub) => (
                                            <option key={sub} value={sub}>{sub}</option>
                                        ))}
                                    </FocusSelect>
                                </div>

                                <div className="col-12 col-md-6">
                                    <label style={labelStyle}>Language</label>
                                    <FocusSelect style={{ ...inputStyle, opacity: 0.7 }} disabled value="English">
                                        <option value="English">English</option>
                                    </FocusSelect>
                                </div>

                                <div className="col-12">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            {...register('is_explicit')}
                                            style={{ width: 16, height: 16, accentColor: 'var(--bs-primary)', flexShrink: 0 }}
                                        />
                                        <span style={{ fontSize: '13px', color: 'var(--bs-secondary-color)' }}>Contains explicit content</span>
                                    </label>
                                </div>
                            </div>
                        </Card>

                        {/* Status & Publishing section */}
                        <Card className="p-4">
                            <p style={sectionHeaderStyle}>Publishing Settings</p>
                            <div className="d-flex flex-column gap-4">
                                {/* Publish toggle */}
                                <div style={{ background: 'rgba(var(--bs-primary-rgb), 0.04)', border: '1px solid rgba(var(--bs-primary-rgb), 0.12)', borderRadius: '12px', padding: '16px' }}>
                                    <div className="d-flex align-items-start justify-content-between gap-3">
                                        <div>
                                            <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--bs-emphasis-color)', marginBottom: 4 }}>Publish this Podcast</p>
                                            <p style={{ fontSize: '12px', color: 'var(--bs-secondary-color)', marginBottom: 0 }}>Make this podcast show publicly visible immediately.</p>
                                        </div>
                                        <div className="form-check form-switch mb-0" style={{ flexShrink: 0 }}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="is_published"
                                                style={{ width: 40, height: 22, cursor: 'pointer', accentColor: 'var(--bs-primary)' }}
                                                {...register('is_published')}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Auto-publish toggle */}
                                <div style={{ background: 'rgba(var(--bs-primary-rgb), 0.04)', border: '1px solid rgba(var(--bs-primary-rgb), 0.12)', borderRadius: '12px', padding: '16px' }}>
                                    <div className="d-flex align-items-start justify-content-between gap-3">
                                        <div>
                                            <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--bs-emphasis-color)', marginBottom: 4 }}>Automatic Publishing</p>
                                            <p style={{ fontSize: '12px', color: 'var(--bs-secondary-color)', marginBottom: 0 }}>
                                                All your audios will be automatically added as podcast episodes when created. You can still remove them later.{' '}
                                                <span style={{ color: 'var(--bs-warning)', fontWeight: 600 }}>This cannot be changed afterwards.</span>
                                            </p>
                                        </div>
                                        <div className="form-check form-switch mb-0" style={{ flexShrink: 0 }}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="is_automatic"
                                                style={{ width: 40, height: 22, cursor: 'pointer', accentColor: 'var(--bs-primary)' }}
                                                {...register('is_automatic')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Right column — poster image */}
                    <div className="col-12 col-lg-4">
                        <Card className="p-4" style={{ position: 'sticky', top: 80 }}>
                            <p style={sectionHeaderStyle}>Podcast Cover Image</p>
                            <p style={{ fontSize: '12px', color: 'var(--bs-secondary-color)', marginBottom: 16, lineHeight: '1.6' }}>
                                Square image, at least 1400×1400px. Supports JPG, PNG, GIF, TIFF up to 4MB.
                            </p>

                            {/* Image picker */}
                            <div
                                onClick={() => document.getElementById('poster').click()}
                                onDragOver={(e) => { e.preventDefault(); setPosterDragOver(true); }}
                                onDragLeave={() => setPosterDragOver(false)}
                                onDrop={handlePosterDrop}
                                style={{
                                    border: `2px dashed ${posterError && !posterFile ? 'var(--bs-danger)' : posterDragOver ? 'var(--bs-primary)' : 'rgba(var(--bs-primary-rgb), 0.25)'}`,
                                    borderRadius: '14px',
                                    background: posterDragOver ? 'rgba(var(--bs-primary-rgb), 0.04)' : 'rgba(var(--bs-primary-rgb), 0.02)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    overflow: 'hidden',
                                    aspectRatio: '1 / 1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                }}
                                onMouseEnter={(e) => { if (!posterFile) e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.50)'; }}
                                onMouseLeave={(e) => { if (!posterFile) e.currentTarget.style.borderColor = (posterError && !posterFile) ? 'var(--bs-danger)' : 'rgba(var(--bs-primary-rgb), 0.25)'; }}
                            >
                                <input
                                    type="file"
                                    id="poster"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={(e) => { setPosterFile(e.target.files[0]); setPosterError(null); }}
                                />
                                {posterFile ? (
                                    <>
                                        <img
                                            src={URL.createObjectURL(posterFile)}
                                            alt="Podcast cover"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                        />
                                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s ease' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.45)'; e.currentTarget.querySelector('span').style.opacity = '1'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0)'; e.currentTarget.querySelector('span').style.opacity = '0'; }}
                                        >
                                            <span style={{ opacity: 0, transition: 'opacity 0.2s ease', color: 'white', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Upload size={14} /> Change Image
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center p-4">
                                        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', boxShadow: '0 4px 14px rgba(var(--bs-primary-rgb), 0.25)' }}>
                                            <Image size={24} color="white" />
                                        </div>
                                        <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--bs-emphasis-color)', marginBottom: 4 }}>Upload Cover Art</p>
                                        <p style={{ fontSize: '11px', color: 'var(--bs-secondary-color)', marginBottom: 0 }}>Click or drag & drop</p>
                                    </div>
                                )}
                            </div>

                            {posterError && !posterFile && (
                                <p style={{ fontSize: '11px', color: 'var(--bs-danger)', marginTop: 8, marginBottom: 0 }}>{posterError}</p>
                            )}

                            {/* Action buttons */}
                            <div className="d-flex gap-2 mt-4">
                                <button
                                    type="button"
                                    onClick={() => history.push(`/organisation/${org_id}/integration/${id}/podcast`)}
                                    style={{ flex: 1, background: 'transparent', border: '1px solid rgba(var(--bs-primary-rgb), 0.20)', color: 'var(--bs-secondary-color)', borderRadius: '10px', padding: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.18s ease' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.40)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.20)'; }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isUpdating}
                                    style={{ flex: 1, background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)', color: 'white', border: 'none', borderRadius: '10px', padding: '10px', fontSize: '13px', fontWeight: 700, cursor: isUpdating ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: '0 4px 14px rgba(var(--bs-primary-rgb), 0.28)', transition: 'all 0.18s ease', opacity: isUpdating ? 0.85 : 1 }}
                                >
                                    {isUpdating ? <Spinner color="white" /> : 'Create Show'}
                                </button>
                            </div>
                        </Card>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default CreatePodcast;
